/**
 * ContentTabsSection — Recent / Top / Sponsored content tabs.
 *
 * Replaces the inline Recent Content render that previously lived in
 * AnalyticsTab.tsx. Same card layout for all three tabs — they share the
 * InsightIQContent shape.
 *
 * Tab visibility:
 *   - Recent  always present when iqProfile.recent_contents has items
 *   - Top     present when iqProfile.top_contents has items
 *   - Sponsored present when iqProfile.sponsored_contents has items
 *
 * Section is fully hidden when none of the three has items (matches the
 * empty-state policy: each section returns null when its data is empty).
 *
 * Default tab is whichever is the first one with data, preferring Recent
 * (matches the prior single-list default behavior).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { InsightIQContent, InsightIQProfile } from '@discovr/types';
import { SectionTitle } from '../charts/StatCard';
import { fmtNum } from '../utils/formatters';
import { CarouselArrows } from './CarouselArrows';

const SCROLL_STEP = 240;

interface ContentTabsSectionProps {
  profile: InsightIQProfile;
}

type TabKey = 'recent' | 'top' | 'sponsored';

interface Tab {
  key: TabKey;
  label: string;
  contents: InsightIQContent[];
}

export function ContentTabsSection({ profile }: ContentTabsSectionProps) {
  const tabs: Tab[] = useMemo(() => {
    const all: Tab[] = [
      { key: 'recent', label: 'Recent', contents: profile.recent_contents ?? [] },
      { key: 'top', label: 'Top', contents: profile.top_contents ?? [] },
      { key: 'sponsored', label: 'Sponsored', contents: profile.sponsored_contents ?? [] },
    ];
    return all.filter((t) => t.contents.length > 0);
  }, [profile.recent_contents, profile.top_contents, profile.sponsored_contents]);

  // Default tab: first one with data (Recent first when available).
  const [requestedKey, setRequestedKey] = useState<TabKey | null>(null);
  const active = tabs.find((t) => t.key === requestedKey) ?? tabs[0];
  const scrollRef = useRef<HTMLDivElement>(null);

  // The carousel keeps its scroll offset across pill switches (same DOM
  // node, new children) — without this, switching tabs while scrolled
  // right opens the new tab mid-scroll instead of at its first card,
  // which reads as "the content didn't update".
  useEffect(() => {
    scrollRef.current?.scrollTo({ left: 0 });
  }, [active?.key]);

  // Empty-state: render nothing when no tab has data.
  if (!active) return null;

  return (
    <div className="rounded-2xl border border-border bg-card p-5 mb-4">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <SectionTitle>Content</SectionTitle>
          <div className="text-body-sm-regular text-[#737373]">Audience Reach &amp; Reputation Overview</div>
        </div>
        <div className="flex items-center gap-2">
          {tabs.length > 1 && (
            // Figma "Segmented Tabs — Assembled": fully rounded (r24) #F4F4F2
            // track + fully rounded white active tab; labels DM Sans 12/400
            // (#171717 active / #737373 idle).
            <div className="flex items-center rounded-full bg-[#F4F4F2] p-1">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setRequestedKey(t.key)}
                  className={`rounded-full px-3 py-1.5 text-body-sm-medium capitalize leading-none transition-colors ${
                    active.key === t.key
                      ? 'bg-white text-[#171717] shadow-[0px_2px_6px_-1px_rgba(0,0,0,0.04),0px_1px_2px_rgba(0,0,0,0.04)]'
                      : 'text-[#737373] hover:text-[#171717]'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
          {/* Figma: horizontal-scroll carousel — left/right scroll arrows */}
          <CarouselArrows scrollRef={scrollRef} step={SCROLL_STEP} />
        </div>
      </div>

      {/* Single row, horizontal scroll (matches Figma — not a wrapping grid) */}
      <div ref={scrollRef} className="flex flex-row gap-3 overflow-x-auto pb-1">
        {active.contents.map((c, i) => (
          <ContentCard key={`${active.key}-${c.url || i}`} content={c} />
        ))}
      </div>
    </div>
  );
}

function ContentCard({ content }: { content: InsightIQContent }) {
  const blurb =
    (content.title && content.title.trim()) ||
    (content.description ?? '').split('\n')[0]?.trim().slice(0, 120) ||
    'Untitled';
  const publishedLabel = content.published_at
    ? new Date(String(content.published_at)).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
      })
    : '';
  const likeCount = content.engagement?.like_count;
  const viewOrPlayCount = content.engagement?.view_count ?? content.engagement?.play_count;
  const shareCount = content.engagement?.share_count;
  const saveCount = content.engagement?.save_count;

  const aria = [
    blurb,
    publishedLabel,
    likeCount != null ? `♥ ${fmtNum(likeCount)}` : null,
    viewOrPlayCount != null ? `▶ ${fmtNum(viewOrPlayCount)}` : null,
    shareCount != null ? `↗ ${fmtNum(shareCount)}` : null,
    saveCount != null ? `🔖 ${fmtNum(saveCount)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // Figma content card: thumbnail → title → tag line → footer (likes · views ·
  // date) pinned to the bottom so cards in a row align regardless of title length.
  return (
    <a
      href={content.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={aria}
      className="group flex w-[220px] flex-none flex-col overflow-hidden rounded-xl border border-border transition-colors"
    >
      <div className="relative aspect-video w-full">
        {content.thumbnail_url ? (
          <img
            src={content.thumbnail_url}
            alt=""
            loading="lazy"
            decoding="async"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
            className="aspect-video w-full object-cover"
          />
        ) : (
          <div className="aspect-video w-full bg-muted" />
        )}
        {/* Decorative play overlay — dark circle + white triangle, centered. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="flex size-8 items-center justify-center rounded-full bg-black/55">
            <svg width="11" height="13" viewBox="0 0 12 14" fill="none" className="ml-0.5">
              <path d="M0 1.3v11.4a1 1 0 0 0 1.5.87l9.8-5.7a1 1 0 0 0 0-1.74L1.5.43A1 1 0 0 0 0 1.3Z" fill="#fff" />
            </svg>
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col p-2.5">
        <div className="line-clamp-2 text-caption-medium/snug font-medium text-foreground">
          {blurb}
        </div>
        {(content.mentions?.length ?? 0) > 0 && (
          <div className="mt-1 line-clamp-1 text-[11px] text-[#6C09F7]">
            {(content.mentions ?? []).slice(0, 3).map((m) => `@${m.name}`).join(' ')}
          </div>
        )}
        {/* Footer pinned to the bottom — likes · views · date */}
        <div className="mt-auto flex items-center gap-3 pt-2.5 text-[11px] text-muted-foreground">
          {likeCount != null && <span className="inline-flex items-center gap-1">♥ {fmtNum(likeCount)}</span>}
          {viewOrPlayCount != null && <span className="inline-flex items-center gap-1">▶ {fmtNum(viewOrPlayCount)}</span>}
          {publishedLabel && <span className="ml-auto">{publishedLabel}</span>}
        </div>
      </div>
    </a>
  );
}
